import { useCallback, useEffect, useState } from "react";
import { Eye, RefreshCw, Search, Send, X } from "lucide-react";

import Api from "../../../../shared/api/api";
import "../styles/AdminEmailHistory.css";

export default function AdminEmailHistory() {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [view, setView] = useState("submissions");
  const [deliveryConfig, setDeliveryConfig] = useState(null);
  const [page, setPage] = useState(1);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      if (view === "deliveries") {
        const response = await Api.getEmailDeliveries({ status, search, page });
        setDeliveryConfig(response);
        setLogs((response?.deliveries || []).map(row => ({ ...row, subject: row.source_type ? `${row.source_type} ${row.source_id || ""}` : "Unmatched provider event", sent_at: row.event_at || row.created_at, message: `Provider ID: ${row.provider_message_id}\nProvider status: ${row.provider_status || "No delivery report received"}\n${row.error_code || ""}\n${row.error_message || ""}` })));
      } else {
        const response = await Api.getEmailLogs({ status, search, limit: 150 });
        setLogs(Array.isArray(response?.logs) ? response.logs : []);
      }
    } catch (requestError) {
      console.error("Unable to load email history:", requestError);
      setError("Email history could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [search, status, view, page]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const viewLog = async (id) => {
    if (view === "deliveries") { setSelectedLog(logs.find(row => row.id === id)); return; }
    try {
      setError("");
      const response = await Api.getEmailLog(id);
      setSelectedLog(response?.log || null);
    } catch (requestError) {
      console.error("Unable to load email log:", requestError);
      setError("That email record could not be loaded.");
    }
  };

  const resendLog = async (log) => {
    const confirmed = window.confirm(
      `Resend "${log.subject}" to ${log.recipient}?`
    );
    if (!confirmed) return;

    try {
      setError("");
      setNotice("");
      setResendingId(log.id);
      const response = await Api.resendEmailLog(log.id);
      setNotice(response?.message || `Email resent to ${log.recipient}.`);
      await loadLogs();
    } catch (requestError) {
      console.error("Unable to resend email:", requestError);
      setError(requestError.message || "The email could not be resent.");
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleString("en-US", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "-";

  return (
    <div className="adminPageContainer email-history-page">
      <div className="email-history-heading">
        <div>
          <h1>Email History</h1>
          <p>{view === "deliveries" ? "Recipient delivery reports" : "Provider submissions"}</p>
        </div>
        <button type="button" className="icon-button" onClick={loadLogs} title="Refresh email history">
          <RefreshCw size={18} aria-hidden="true" />
          <span className="sr-only">Refresh email history</span>
        </button>
      </div>

      <div role="tablist" aria-label="Email history views" className="email-history-actions">
        {[["submissions", "Submissions"], ["deliveries", "Delivery reports"]].map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => { setView(key); setStatus(""); setSearch(""); setPage(1); setSelectedLog(null); }}>{label}</button>)}
      </div>
      {view === "deliveries" && deliveryConfig && !deliveryConfig.events_configured && <p role="status" className="email-history-notice">Azure delivery-event connection is pending. Accepted submissions are not proof of delivery.</p>}

      <form
        className="email-history-filters"
        onSubmit={(event) => {
          event.preventDefault();
          loadLogs();
        }}
      >
        <label>
          Status
          <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
            <option value="">All</option>
            {(view === "deliveries" ? ["pending", "accepted", "delivered", "unknown", "submission_failed", "bounced", "suppressed", "failed", "quarantined", "filteredspam", "expanded"] : ["sent", "failed"]).map(value => <option key={value} value={value}>{value === "sent" ? "Accepted / legacy sent" : value.replace(/_/g, " ")}</option>)}
          </select>
        </label>
        <label className="email-search-field">
          Search
          <span>
            <Search size={17} aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              placeholder={view === "deliveries" ? "Recipient email" : "Recipient, subject, or order number"}
            />
          </span>
        </label>
        <button type="submit">Search</button>
      </form>

      {error && <p className="email-history-error" role="alert">{error}</p>}
      {notice && <p className="email-history-notice" role="status">{notice}</p>}
      {loading ? (
        <p>Loading email history...</p>
      ) : (
        <div className="email-history-table-wrap">
          <table className="email-history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Status</th>
                <th>Recipient</th>
                <th>{view === "deliveries" ? "Source" : "Subject"}</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{formatDate(log.sent_at)}</td>
                  <td><span className={`email-status ${log.status}`}>{view === "submissions" && log.status === "sent" ? "accepted / legacy sent" : log.status.replace(/_/g, " ")}</span></td>
                  <td>{log.recipient}</td>
                  <td>{log.subject}</td>
                  <td>
                    <div className="email-history-actions">
                      <button type="button" className="icon-button" onClick={() => viewLog(log.id)} title="View email record">
                        <Eye size={18} aria-hidden="true" />
                        <span className="sr-only">View email record</span>
                      </button>
                      {view === "submissions" && <button
                        type="button"
                        className="icon-button"
                        onClick={() => resendLog(log)}
                        disabled={resendingId === log.id}
                        title="Resend email"
                      >
                        <Send size={18} aria-hidden="true" />
                        <span className="sr-only">Resend email</span>
                      </button>}
                    </div>
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan="5">No email records match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {view === "deliveries" && <div className="email-history-actions"><button type="button" disabled={loading || page === 1} onClick={() => setPage(value => value - 1)}>Previous</button><span>Page {page}</span><button type="button" disabled={loading || logs.length < 50} onClick={() => setPage(value => value + 1)}>Next</button></div>}

      {selectedLog && (
        <div className="email-log-overlay" role="presentation" onClick={() => setSelectedLog(null)}>
          <section className="email-log-dialog" role="dialog" aria-modal="true" aria-labelledby="email-log-title" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="icon-button email-log-close" onClick={() => setSelectedLog(null)} title="Close email record">
              <X size={20} aria-hidden="true" />
              <span className="sr-only">Close email record</span>
            </button>
            <h2 id="email-log-title">{selectedLog.subject}</h2>
            <dl>
              <div><dt>Status</dt><dd>{selectedLog.status}</dd></div>
              <div><dt>Recipient</dt><dd>{selectedLog.recipient}</dd></div>
              <div><dt>Date</dt><dd>{formatDate(selectedLog.sent_at)}</dd></div>
            </dl>
            <pre>{selectedLog.message || "No message details were recorded."}</pre>
          </section>
        </div>
      )}
    </div>
  );
}
